import { LocateFixed } from "lucide-react";
import ResponsiveModal from "./ResponsiveModal";
import { Button } from "./ui/button";
import DeliveryGuy from "../../public/deliveryGuy.json";
import Lottie from "lottie-react";
import { useLocation } from "@/contexts/LocationProvider";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function LocationModal({ open, onClose }: LocationModalProps) {
  const { setLocation, hasLocation, address, street, streetNumber, locate } =
    useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const { i18n, t } = useTranslation();

  useEffect(() => {
    reset({
      address: address,
      street: street,
      streetNumber: streetNumber,
    });
  }, [address, street, streetNumber]);

  const onSubmit = (data: any) => {
    setLocation(data.address, data.street, data.streetNumber);
    toast.success("Address added successfully");
    onClose();
  };

  const onLocate = async () => {
    const { address, street, streetNumber } = await locate();

    reset({
      address: address,
      street: street,
      streetNumber: streetNumber,
    });
  };

  const renderContent = () => {
    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col min-h-[400px] max-h-[85vh] p-6 sm:p-8 gap-4"
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="text-center space-y-2 shrink-0">
          <h1 className="text-2xl font-bold text-gray-900">
            {hasLocation ? t("edit_address") : t("add_new_address")}
          </h1>
        </div>
        {/* Input */}
        <div className="flex flex-col gap-1 shrink-0">
          <label htmlFor="phone" className="text-sm font-medium text-gray-700">
            {t("address")}
          </label>
          <div className="relative w-full">
            <input
              autoComplete="off"
              {...register("address", { required: "address is required" })}
              placeholder={t("enter_address")}
              className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-10 focus:outline-none focus:ring-2 focus:ring-primary"
            />

            {/* Icon inside input at the end */}
            <LocateFixed
              className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer hover:text-primary transition-all duration-300"
              size={20}
              onClick={() => {
                onLocate();
              }}
            />
          </div>
          {errors.address && (
            <p className="text-red-500 text-sm">
              {errors.address.message as string}
            </p>
          )}
        </div>
        <div className="flex flex-row gap-4 shrink-0">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="phone"
              className="text-sm font-medium text-gray-700"
            >
              {t("street")}
            </label>
            <input
              autoComplete="off"
              {...register("street", { required: "street is required" })}
              placeholder={t("enter_street")}
              className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-10 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.street && (
              <p className="text-red-500 text-sm">
                {errors.street.message as string}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="phone"
              className="text-sm font-medium text-gray-700"
            >
              {t("street_number")}
            </label>
            <input
              autoComplete="off"
              {...register("streetNumber", {
                required: "streetNumber is required",
              })}
              placeholder={t("enter_street_number")}
              className="w-full rounded-lg border border-gray-300 py-2 ps-3 pe-10 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.streetNumber && (
              <p className="text-red-500 text-sm">
                {errors.streetNumber.message as string}
              </p>
            )}
          </div>
        </div>
        <div className="w-full flex-1 mx-auto min-h-0">
          <Lottie
            className="w-full h-full"
            animationData={DeliveryGuy}
            loop={true} // loop the animation
            autoplay={true} // auto start
          />
        </div>
        <Button size="lg" className="mt-auto shrink-0 mb-2" type="submit">
          {t("save")}
        </Button>
      </form>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      renderContent={renderContent}
    />
  );
}
